import React, { useEffect, useState } from "react";
import { Storage, DataStore } from 'aws-amplify';
import { AppUser } from "../../models";
import { StyleSheet, Text, TouchableOpacity, View, SafeAreaView, TextInput, Platform, Image } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as CmnUtil from '../components/CmnUtil';
import { SaveDialog } from "../components/CmnDiaLog";
import { appAlert, appMsg, fillZero, getCurrentUser } from "../components/CmnUtil";
import { Header } from "../components/Header";
import IconImages from "../components/IconImages";

/** ================================
*  ユーザ情報変更画面
*  ================================*/
const UserInfoUpdate = ({ navigation }: { navigation: any; }) => {

  // ユーザー情報
  const [userInfo, setUserInfo] = useState<AppUser | undefined>(undefined);

  // ユーザー名格納State
  const [userName, setUserName] = useState<string>("");

  // プロフィール格納State
  const [profile, setProfile] = useState<string | undefined>("");

  // アイコン格納State
  const [icon, setIcon] = useState<string | undefined>(undefined);

  // アイコンのキー格納State
  const [oldIconKey, setOldIconKey] = useState<string>("");

  // 保存ダイアログ表示State
  const [saveDialogFlag, setSaveDialogFlag] = useState<boolean>(false);

  // ログインユーザーID保存State
  const [currentUserId, setCurrentUserId] = useState<string>("");

  /**
   * ステートの初期化 
   */
  const init = async () => {
    (async () => {
      const appUser = await getCurrentUser();
      if (appUser == undefined) {
        setUserInfo(undefined);
        setUserName("");
        setProfile("");
        setOldIconKey("");
        setSaveDialogFlag(false);
        setCurrentUserId("");
      } else {
        setUserInfo(appUser);
        setCurrentUserId(appUser.id);
        setUserName(appUser.username);
        setProfile(appUser.profile ? appUser.profile : "");
        if (appUser.icon != undefined) {
          setOldIconKey(appUser.icon);
        };
      };
    })();
  };

  // ユーザー情報変更処理
  const updateAppUser = async (iconKey: string) => {
    let msgText: string[] = ["", ""];
    const appUser = await getCurrentUser();
    if (appUser) {
      await DataStore.save(
        AppUser.copyOf(appUser, updated => {
          updated.username = userName;
          updated.profile = profile;
          updated.icon = icon ? iconKey : updated.icon;
        })
      )
        .then(() => msgText = ["success", "情報を変更しました。"])
        .catch(() => msgText = ["error", "変更に失敗しました。"]);
    } else {
      msgText = ["error", "変更に失敗しました。"];
    }
    return msgText;
  };

  // S3にアイコンを保存する処理
  const pathToImageFile = async (iconKey: string) => {
    let msgText = ["", ""];
    if (!icon) {
      return ["success", "処理SKIP"];
    }
    try {
      const response = await fetch(icon);
      const blob = await response.blob();
      await Storage.put(iconKey, blob, {
        contentType: "image/jpeg", // contentType is optional
      })
        .then(() => msgText = ["success", "S3に新しく画像を保存しました。"])
        .catch(() => msgText = ["error", "S3に画像を保存できませんでした。"]);
    } catch (err) {
      msgText = ["error", "保存に失敗しました。"];
    };
    return msgText;
  };

  // S3からアイコンのキーを削除する処理
  const deleteIconKey = async (iconKey: string) => {
    let msgText: string[] = ["", ""];

    if (!userInfo?.icon || !icon) {
      return ["success", "ユーザー情報を変更しました。"];
    }

    await Storage.remove(iconKey)
      .then(() => msgText = ["success", "ユーザー情報を変更しました。"])
      .catch(() => msgText = ["error", "S3の古いアイコンの削除に失敗しました。"]);

    return msgText;
  };

  // S3のアイコンキーを作成
  const createIconKey = (): string => {
    const now = new Date();
    let key: string = currentUserId + "/user_icon/icon_";
    key += fillZero(now.getFullYear(), 4);
    key += fillZero(now.getMonth() + 1, 2);
    key += fillZero(now.getDate(), 2);
    key += fillZero(now.getHours(), 2);
    key += fillZero(now.getMinutes(), 2);
    key += fillZero(now.getSeconds(), 2);
    return key;
  };

  // 画面起動時に初期化
  useEffect(() => {
    init();
  }, []);

  // アクセス許可
  useEffect(() => {
    (async () => {
      if (Platform.OS !== "web") {
        // 写真にアクセスする許可を求める
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== "granted") {
          // アクセスできなければアラート
          alert("Sorry, we need camera roll permissions to make this work!");
        };
      };
    })();
  }, []);

  // アイコン変更ボタンクリック
  const pickImage = async () => {
    // 携帯電話のライブラリから画像を選択するシステムUIを表示
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowEditiong: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.cancelled) {
      setIcon(result.uri);
    };
  };

  // 保存ダイアログのボタンを押したときの処理
  const onClickDialogButton = async () => {
    setSaveDialogFlag(false);

    const iconKey = createIconKey();
    let msgText: string[] = ["error", "例外が発生しました。"];

    // 保存ボタンクリック→保存処理
    if (userName === "") {
      msgText[1] = "ユーザー名は必須入力です。";
    } else {
      // S3に新しいキーを保存
      await pathToImageFile(iconKey)
        .then(res => msgText = res);

      if (msgText[0] == "success") {
        // AppUserに新しいキーを保存
        await updateAppUser(iconKey)
          .then(res => msgText = res);

        if (msgText[0] == "success") {
          // S3から古いキーを削除
          await deleteIconKey(oldIconKey)
            .then(res => msgText = res);
        };
      };
    };

    if (msgText[0] == "success") {
      await init();
      await appMsg(msgText[1]);
    } else if (msgText[0] == "error") {
      await appAlert(msgText[1]);
    };
  };

  return (
    <SafeAreaView style={styles.container}>
      <SaveDialog
        cancelButtonName={"キャンセル"}
        saveButtonName={"保存"}
        canceAction={() => setSaveDialogFlag(false)}
        saveAction={() => onClickDialogButton()}
        message={"変更を保存しますか？"}
        visible={saveDialogFlag}
      />

      <Header onPress={() => navigation.goBack()} goBackText="＜" headerText="ユーザー情報変更" />

      <View style={styles.formBlock}>
        <Text style={styles.formText}>ユーザー名</Text>
        <TextInput style={styles.formInputName} value={userName} onChangeText={setUserName} />
      </View>
      <View style={styles.formBlock}>
        <Text style={styles.formText}>プロフィール</Text>
        <TextInput multiline numberOfLines={4} editable style={styles.formInputProfile} value={profile} onChangeText={setProfile} />
      </View>
      <View style={styles.formBlock}>
        <Text style={styles.formText}>アイコン</Text>
        <View style={styles.iconWrap}>
          {icon ?
            <Image resizeMode="contain" source={{ uri: icon }} style={styles.userIcon} defaultSource={IconImages.icUser} />
            :
            <CmnUtil.S3Image imageKey={userInfo?.icon} style={styles.userIcon} defaultSource={IconImages.icUser} />
          }
          <TouchableOpacity style={{ marginLeft: 20 }} onPress={pickImage}>
            <Text style={{ fontSize: 18, textDecorationLine: "underline" }}>アイコンを変更する</Text>
          </TouchableOpacity>
        </View>
      </View>
      <TouchableOpacity style={styles.button} onPress={() => setSaveDialogFlag(true)}>
        <Text style={styles.buttonText}>保存</Text>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#E2F0D9",
    position: "relative",
  },
  button: {
    backgroundColor: "#7AAD47",
    borderWidth: 1,
    borderColor: "#057E32",
    borderStyle: "solid",
    width: 120,
    marginLeft: "auto",
    marginRight: "auto",
    height: 40,
    justifyContent: 'center',
    shadowColor: "#808080",
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
  },
  buttonText: {
    color: "#ffffff",
    textAlign: "center",
    fontSize: 18,
  },
  formBlock: {
    marginBottom: 15,
  },
  formInputName: {
    backgroundColor: "#FFFFFF",
    height: 40,
    fontSize: 18,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#417B9C",
    paddingHorizontal: 25,
  },
  formInputProfile: {
    backgroundColor: "#FFFFFF",
    height: 80,
    fontSize: 18,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "#417B9C",
    paddingHorizontal: 25,
    paddingVertical: 5,
  },
  formText: {
    fontSize: 18,
    marginBottom: 5,
    marginLeft: 20,
  },
  userIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginLeft: 20,
    backgroundColor: "#808080",
  },
  iconWrap: {
    flexDirection: "row",
    alignItems: "flex-end",
  },
});

export default UserInfoUpdate;